import Text from '$/components/ui/Text';
import { BillingCycle } from '$/GraphQL/schema/types';
import EDate from '$/shared/frontend/utils/EDate';
import { UpdateAddOnType } from '../AddOnsContext';
import { Per } from '../Components';
import { AddOn } from './AddOn';

export class UniversalDashboard extends AddOn {
  public get shortDescription(): string {
    return 'Track individual registrants and run analytics across all or any combo of webinars, plus answer chat for all your assigned webinars in one place.';
  }

  public get description(): string {
    return 'Track individual registrants and run analytics across all or any combo of webinars, plus answer chat for all your assigned webinars in one place.';
  }

  public image(): string | null {
    return require('$/images/logos/universal-dashboard.png').default;
  }

  public getModalMessage(
    type: UpdateAddOnType,
    billingCycle: BillingCycle,
    totalPublished: number,
    freeUntil?: EDate
  ): React.ReactElement {
    if (type === UpdateAddOnType.Unsubscribe && this.isTrialActive && this.trialEnd) {
      const formattedPrice = this.getFormattedPrice(billingCycle);
      return (
        <Text.body>
          <p>
            If you unsubscribe from the <b>{this.name} add-on</b>, your subscription to it will
            remain active until the end of your free trial on <b>{this.trialEnd.format('P')}</b>. If
            you resubscribe before that time, your access to this feature will remain uninterrupted.
          </p>

          <p>
            Please note if you stay subscribed to this add-on, when your trial ends you will be
            charged a prorated amount at the rate of{' '}
            <b>
              {formattedPrice}
              <Per billingCycle={billingCycle} />
            </b>{' '}
            for the time remaining on your current billing cycle. .
          </p>

          <p>Do you wish to continue?</p>
        </Text.body>
      );
    }

    if (type === UpdateAddOnType.Subscribe && totalPublished < 2) {
      return (
        <Text.body>
          <p>You must have at least 2 active webinars to subscribe to this add-on.</p>
        </Text.body>
      );
    }

    if (type !== UpdateAddOnType.Subscribe || !this.isTrialAvailable) {
      return super.getModalMessage(type, billingCycle, totalPublished, freeUntil);
    }

    const trialDays = this.trialDays;
    const trialEndDate = new EDate(new Date(), 'UTC').addDays(trialDays);
    const formattedPrice = this.getFormattedPrice(billingCycle);
    const billingCycleText = billingCycle === BillingCycle.Month ? 'monthly' : 'annual';
    return (
      <Text.body>
        <p>
          If you subscribe to the <b>{this.name} add-on</b>, your {billingCycleText} subscription
          rate will not increase right away. Instead, the add-on will be free for you until{' '}
          <b>{trialEndDate.format('P')}</b>, at which time your total subscription rate will
          increase by{' '}
          <b>
            {formattedPrice}
            <Per billingCycle={billingCycle} />
          </b>
          , and you will be charged a prorated amount at that rate for the time remaining in your
          current subscription term.
        </p>
        <p className='font-semibold'>
          Once charged, add-ons are non-refundable for the billing cycle in question. You may
          unsubscribe from them at any time.
        </p>

        {this.disclosure}

        <p>Do you wish to continue?</p>
      </Text.body>
    );
  }

  public isDisabledModalSubmit(
    type: UpdateAddOnType,
    totalPublished: number
  ): { buttonText: string } | undefined {
    if (type !== UpdateAddOnType.Subscribe) {
      return undefined;
    }
    if (totalPublished < 2) {
      return {
        buttonText: 'Got it!',
      };
    }
    return undefined;
  }
}
